// usage_banner_v1
// แสดงโควต้าของ tenant — channels/agents/kb พร้อม progress + Upgrade CTA
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, Zap } from 'lucide-react';
import API from '../api';

// resource: 'channels' | 'agents' | 'kb' | 'slip_quota'
// labels per resource
const LABELS = {
  channels: { icon: '📡', name: 'Social Accounts', singular: 'channel' },
  agents:   { icon: '👤', name: 'Admin', singular: 'admin' },
  kb:       { icon: '🤖', name: 'KB (Bot Training)', singular: 'KB' },
  slip_quota: { icon: '💳', name: 'Slip / เดือน', singular: 'slip' },
  telegrams: { icon: '📲', name: 'Telegram Bots', singular: 'Telegram' } /* telegram_usage_banner_v1 */
};

export default function UsageBanner({ resource, compact = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/api/me/usage')
      .then(({ data }) => setUsage(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !usage) return null;

  const data = usage[resource];
  if (!data) return null;

  const label = LABELS[resource] || { icon: '📊', name: resource, singular: resource };
  const { used, max, percent, full } = data;

  // Color scheme based on % usage
  let bg, border, textColor, barColor, status, statusIcon;
  if (full) {
    bg = '#fef2f2'; border = '#fca5a5'; textColor = '#991b1b';
    barColor = '#dc2626'; status = 'เต็มแล้ว — ต้องอัพเกรด'; statusIcon = '🚨';
  } else if (percent >= 80) {
    bg = '#fffbeb'; border = '#fcd34d'; textColor = '#92400e';
    barColor = '#f59e0b'; status = 'ใกล้เต็มแล้ว'; statusIcon = '⚠️';
  } else if (percent >= 60) {
    bg = '#eff6ff'; border = '#93c5fd'; textColor = '#1e40af';
    barColor = '#3b82f6'; status = 'ใช้งานปกติ'; statusIcon = '📊';
  } else {
    bg = '#f0fdf4'; border = '#86efac'; textColor = '#166534';
    barColor = '#22c55e'; status = 'มีพื้นที่เหลือเยอะ'; statusIcon = '✅';
  }

  if (compact) {
    return (
      <div style={{
        background: bg, border: `1px solid ${border}`, borderRadius: 8,
        padding: '6px 12px', display: 'inline-flex', alignItems: 'center',
        gap: 8, fontSize: 13, color: textColor
      }}>
        <span>{label.icon}</span>
        <b>{used}/{max}</b>
        <span>•</span>
        <span>{percent}%</span>
      </div>
    );
  }

  return (
    <div style={{
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, color: textColor, fontWeight: 600, marginBottom: 4 }}>
            {label.icon} {label.name} — <b>{used}/{max}</b> ({percent}%)
          </div>
          <div style={{ fontSize: 12, color: textColor, opacity: 0.85 }}>
            {statusIcon} {status}
            {' '}<span style={{ marginLeft: 6, opacity: 0.7 }}>• Plan: {usage.plan}</span>
          </div>
        </div>
        {(full || percent >= 80) && (
          <button
            onClick={() => navigate('/renew')}
            style={{
              background: full ? '#dc2626' : '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap'
            }}
          >
            <Zap size={14} />
            {full ? 'อัพเกรดด่วน!' : 'อัพเกรด Plan'}
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div style={{
        marginTop: 12,
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.05)',
        borderRadius: 999,
        height: 10,
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div style={{
          width: `${Math.min(percent, 100)}%`,
          height: '100%',
          background: barColor,
          borderRadius: 999,
          transition: 'width 0.5s ease'
        }} />
      </div>

      {full && (
        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          background: '#fff',
          borderRadius: 6,
          fontSize: 12,
          color: '#991b1b',
          borderLeft: '3px solid #dc2626'
        }}>
          <AlertTriangle size={12} style={{ display: 'inline', marginRight: 4 }} />
          คุณใช้ {label.singular} ครบจำนวนที่ plan รองรับแล้ว — อัพเกรด plan เพื่อเพิ่มได้
        </div>
      )}
    </div>
  );
}

// Helper hook to know if a resource is full (for disabling buttons)
export function useUsageFull(resource) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    API.get('/api/me/usage')
      .then(({ data }) => {
        if (data && data[resource]) setFull(!!data[resource].full);
      })
      .catch(() => {});
  }, [resource]);
  return full;
}
